// externals
import type { RSAAAction } from 'redux-api-middleware';
import { RSAA } from 'redux-api-middleware';

// libraries
import type {
    CreditRatingDisplayType,
    StateTree,
    PendingReviewContext,
    PendingReviewResolution,
    PendingReviewResolutionPath
} from '@makemydeal/dr-dash-types';
import { paymentActionTypes, utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import type { FeeOverride, ManualIncentiveEntry, OfferType } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { buildPaymentGridBaggageForSave } from '../utils/paymentGridBaggageUtils';
import { MANAGER_VIEW_APP_PREFIX, apiConfigHelper, locHrefUtil } from '@makemydeal/dr-shared-ui-utils';

// interfaces/types
import type { SavedOfferPayloadObject } from '../types/comparisonUtilsTypes';
import type { DashStoreThunk } from '../types/appState';

// selectors
import * as offerSelectors from '../selectors/offerSelectors';

import * as offerRedux from './offerRedux';
import { getActiveComparison } from '../selectors/multiScenarioSelectors';

// consts/enums
import {
    CLEAR_SOCKET_EVENT_NOTIFICATION,
    COBUYER_SHOW_INVALID_MODAL,
    MARK_GTL_BANNER_SHOWN_ON_LOAD,
    SET_PENDING_REVIEW_CONTEXT,
    SET_PENDING_REVIEW_RESOLUTION_PATH,
    MARK_PENDING_REVIEW_HANDLED,
    SET_PENDING_REVIEW_TARGET_VERSION,
    SET_PENDING_REVIEW_RESOLUTION,
    CLEAR_PENDING_REVIEW,
    RESOLVE_PENDING_REVIEW_REQUESTED,
    RESOLVE_PENDING_DEALER_REVIEW_PAYMENT,
    OFFER_SAVE_FAILURE,
    OFFER_SAVE_NOCHANGE_REQUEST,
    OFFER_SAVE_NOCHANGE_SUCCESS,
    OFFER_SAVE_REQUEST,
    OFFER_SAVE_SUCCESS,
    SAVE_OFFER,
    SEND_TO_DEALER_TRACK,
    SET_CREDIT_RATING_DISPLAY_TYPE,
    SET_SHOW_SOFT_PULL_CONSENT_MODAL,
    SET_SOFT_PULL_CONSENT_PROVIDED,
    UPDATED_MANUAL_INCENTIVES,
    UPDATED_TOTAL_TAX_OVERRIDE,
    UPDATED_TOTAL_TAX_UPFRONT_OVERRIDE,
    UPDATE_DEAL_REF_ID,
    UPDATE_PAYMENT_FEES_OVERRIDE,
    UPDATED_ACQFEE_OVERRIDE,
    UPDATED_MAX_RESIDUALIZED_MSRP_OVERRIDE,
    UPDATED_SECURITY_DEPOSIT_OVERRIDE
} from '../actionTypes/offerActionTypes';

// utils
import { FeesCategoriesType } from '@makemydeal/dr-shared-types';
import { ACQUISITION_FEE_ID } from '../constants';
import { getCurrentOfferType, getFeesOverride, getIsUserProgramQuotes } from '../selectors/offerRedux';
import { isExplicitLenderP1Active } from '../selectors/programsSelectors';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import {
    SET_IS_PAYMENT_CALL_PENDING,
    SET_PAYMENT_IN_FLIGHT_COUNT,
    UPDATE_DEFERRED_DOWN_PAYMENT
} from '../actionTypes/baseDealActionTypes';

export type saveOfferRSAAOptions = SaveOfferOptions & {
    state: StateTree;
    offerSavePayload: SavedOfferPayloadObject;
    offerSavePayloadIndex?: number;
    saveStartTime?: number;
};

export type SaveOfferOptions = {
    source?: string;
    isDraft?: boolean;
    originalAction?: string | AnyFSA;
    templateId?: string;
    isPrimaryScope?: boolean;
    clearDealRefId?: boolean;
};

export const saveOffer = (opts?: SaveOfferOptions) => {
    const action: AnyFSA = {
        type: SAVE_OFFER,
        meta: {
            saveStartTime: performance.now(),
            ...(opts?.source && { source: opts.source }),
            ...(opts?.isDraft != null && { isDraft: opts.isDraft }),
            ...(opts?.originalAction && { originalAction: opts.originalAction }),
            ...(opts?.templateId && { templateId: opts.templateId })
        }
    };
    return action;
};

export const sendToDealerTrackClicked = () => {
    return {
        type: SEND_TO_DEALER_TRACK
    };
};

export const saveOfferRSAA = (options: saveOfferRSAAOptions) => {
    const {
        state,
        offerSavePayload,
        offerSavePayloadIndex,
        source,
        isDraft = false,
        clearDealRefId = false,
        saveStartTime
    } = options;
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, 'offer/save');
    const endpoint = bffApiUrlResult.url;
    const dmsIntegrationToggle = dealerSelectors.getDMSIntegrationToggle(state);

    if (isDraft) {
        if (offerSavePayload.offer.dealExchangeVersionId?.startsWith('SCENARIO-')) {
            const comparison = getActiveComparison(state);
            if (comparison) {
                offerSavePayload.offer.dealExchangeVersionId = comparison.dealXgVersionId;
            }
        }
    }

    /**
     * US2041815: the compressed payment grid is attached here, on the save itself, and not inside
     * `buildMultipleOfferSavePayload`. That builder also backs `currentOfferHasUnsavedChanges`, which
     * deep-diffs two builds of the payload — and this field is time-dependent, so building it there
     * reported unsaved changes forever and gzipped every scenario's grid on a selector path.
     */
    const paymentGridBaggage = buildPaymentGridBaggageForSave(state, offerSavePayload);
    const proposalId = typeof options.originalAction === 'object' ? options.originalAction.payload?.selectedProposalId : undefined;
    const savePayload = {
        ...offerSavePayload,
        ...(paymentGridBaggage && { paymentGridBaggage }),
        ...(proposalId && { proposalId })
    };

    return <RSAAAction>{
        [RSAA]: {
            endpoint,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                isDraft: String(isDraft),
                'x-clear-deal-refid': String(clearDealRefId)
            },

            body: JSON.stringify(savePayload),
            types: [
                { type: OFFER_SAVE_REQUEST, meta: { isDraft, source } },
                {
                    type: OFFER_SAVE_SUCCESS,
                    meta: {
                        source,
                        isDraft,
                        offerSavePayloadIndex,
                        dmsIntegrationToggle,
                        offerSavePayload,
                        templateId: options.templateId,
                        originalAction: options.originalAction,
                        saveStartTime
                    }
                },
                {
                    type: OFFER_SAVE_FAILURE,
                    meta: {
                        source,
                        isDraft,
                        offerSavePayloadIndex,
                        dmsIntegrationToggle,
                        originalAction: options.originalAction,
                        saveStartTime
                    }
                }
            ]
        }
    };
};

export const offerSaveNoChangeRequest = (
    dealXgId: string | undefined,
    dealXgVersion: string | undefined,
    source: string | undefined
) => ({
    type: OFFER_SAVE_NOCHANGE_REQUEST,
    payload: {
        dealXgId,
        dealXgVersion
    },
    meta: {
        source
    }
});

export const offerSaveNoChangeSuccess = (
    dealXgId: string | undefined,
    dealXgVersion: string | undefined,
    source: string | undefined
) => ({
    type: OFFER_SAVE_NOCHANGE_SUCCESS,
    payload: {
        dealXgId,
        dealXgVersion
    },
    meta: {
        source
    }
});

const syncsExplicitLenderAcqFee = (state: StateTree): boolean =>
    getCurrentOfferType(state) === LEASE && isExplicitLenderP1Active(state);

export const syncedAcqFeeOverride = (amount: number, upFront: boolean) => ({
    type: UPDATED_ACQFEE_OVERRIDE,
    payload: { amount, upFront },
    meta: { middleware: { payment: false } }
});

export const syncedSecurityDepositOverride = (amount: number) => ({
    type: UPDATED_SECURITY_DEPOSIT_OVERRIDE,
    payload: amount,
    meta: { middleware: { payment: false } }
});

export const syncedMaxResidualizedMsrpOverride = (maxResidualizedMsrp: number, maxResidualizedMsrpApplied: boolean) => ({
    type: UPDATED_MAX_RESIDUALIZED_MSRP_OVERRIDE,
    payload: { maxResidualizedMsrp, maxResidualizedMsrpApplied },
    meta: { middleware: { payment: false } }
});

export const withAcqFeeEntry = (
    feesOverride: FeeOverride[] = [],
    changes: Partial<FeeOverride>,
    amount: number,
    reqCapIndicator: boolean
): FeeOverride[] => {
    const index = feesOverride.findIndex((fee) => fee.feeTypeId === ACQUISITION_FEE_ID);
    if (index === -1) {
        return [
            ...feesOverride,
            {
                feeTypeId: ACQUISITION_FEE_ID,
                feeAmount: amount,
                reqCapIndicator,
                feeDescription: '',
                feeName: 'Acquisition Fee',
                category: FeesCategoriesType.LENDER,
                includeInCalcIndicator: true
            }
        ];
    }
    return feesOverride.map((fee, feeIndex) => (feeIndex === index ? { ...fee, ...changes } : fee));
};

export const updateAcqFeeOverride = (amount: number, heldUpFront?: boolean) => (dispatch: any, getState: any) => {
    const acqFeeUpFrontOverride = heldUpFront ?? offerSelectors.getAcqFeeUpFrontOverrideWithFallback(getState());
    if (syncsExplicitLenderAcqFee(getState())) {
        dispatch(syncedAcqFeeOverride(amount, acqFeeUpFrontOverride));
    }
    const feesOverride = withAcqFeeEntry(getFeesOverride(getState()), { feeAmount: amount }, amount, !acqFeeUpFrontOverride);
    dispatch(updatedFeesOverride(feesOverride));
};

export const updateAcqFeeUpFrontOverride = (enabled: boolean, heldAmount?: number) => (dispatch: any, getState: any) => {
    const amount = heldAmount ?? offerSelectors.getAcqFeeOverrideWithFallback(getState());
    if (syncsExplicitLenderAcqFee(getState())) {
        dispatch(syncedAcqFeeOverride(amount, enabled));
    }
    const feesOverride = withAcqFeeEntry(getFeesOverride(getState()), { reqCapIndicator: !enabled }, amount, !enabled);
    dispatch(updatedFeesOverride(feesOverride));
};

export const applyProgramEndDays = () => ({
    type: paymentActionTypes.PROGRAMS_END_DAYS,
    payload: 7
});

export const updateMaxResidualizedMsrpOverride = (maxResidualizedMsrp: number) => (dispatch: any, getState: any) => {
    const maxResidualizedMsrpApplied = offerSelectors.getMaxResidualizedMsrpAppliedOverrideWithFallback(getState());
    dispatch(offerRedux.updateMaxResidualizedMsrpOverride(maxResidualizedMsrp, maxResidualizedMsrpApplied));
};

export const updateManualIncentives = (params: {
    manualIncentives: Array<Record<string, unknown>> | undefined;
    dealerCashTotal: number;
    totalRebates: number;
    skipPayment?: boolean;
    preserveSelectedIncentives?: boolean;
}): AnyFSA => {
    const { manualIncentives, dealerCashTotal, totalRebates, skipPayment, preserveSelectedIncentives } = params;

    const payload =
        manualIncentives == null
            ? { manualIncentives: undefined, preserveSelectedIncentives }
            : {
                  manualIncentives: {
                      dealerCash: dealerCashTotal,
                      incentives: manualIncentives,
                      totalRebates
                  },
                  preserveSelectedIncentives
              };

    return {
        type: UPDATED_MANUAL_INCENTIVES,
        payload,
        ...(!skipPayment && {
            meta: {
                middleware: {
                    payment: true
                }
            }
        })
    };
};

export const retainManualIncentives =
    (retainedIncentives: ManualIncentiveEntry[]): DashStoreThunk =>
    (dispatch) => {
        dispatch(
            updateManualIncentives({
                manualIncentives: retainedIncentives,
                dealerCashTotal: offerReduxUtils.incentives.getIncentiveSum(
                    retainedIncentives.filter(offerReduxUtils.incentives.isDealerCash)
                ),
                totalRebates: offerReduxUtils.incentives.getIncentiveSum(
                    retainedIncentives.filter(offerReduxUtils.incentives.isNotDealerCash)
                ),
                skipPayment: true
            })
        );
    };

export const updateShowCoBuyerInvalidModal = (visible: boolean) => ({
    type: COBUYER_SHOW_INVALID_MODAL,
    payload: visible
});

export const markGtlBannerShownOnLoad = () => ({
    type: MARK_GTL_BANNER_SHOWN_ON_LOAD
});

// Releases any resolution held for the previous review — the decision screen will not open while one is
// set. To fill in a deferred branch on the review already in flight, use setPendingReviewResolutionPath.
export const setPendingReviewContext = (context: PendingReviewContext) => ({
    type: SET_PENDING_REVIEW_CONTEXT,
    payload: context
});

// Leaves the rest of the context, and any resolution recorded against it, untouched.
export const setPendingReviewResolutionPath = (resolutionPath: PendingReviewResolutionPath) => ({
    type: SET_PENDING_REVIEW_RESOLUTION_PATH,
    payload: resolutionPath
});

// Written when the dealer is *prompted*, not when the resolution completes: both apply paths re-dispatch
// the bootstrap while the record is still pending, so detection re-runs and must find this version
// already handled or the dealer is prompted twice.
export const markPendingReviewHandled = (dealXgVersion: string) => ({
    type: MARK_PENDING_REVIEW_HANDLED,
    payload: dealXgVersion
});

export const setPendingReviewTargetVersion = (dealXgVersion: string) => ({
    type: SET_PENDING_REVIEW_TARGET_VERSION,
    payload: dealXgVersion
});

export const setPendingReviewResolution = (resolution: PendingReviewResolution) => ({
    type: SET_PENDING_REVIEW_RESOLUTION,
    payload: resolution
});

// For when the dealer applies a version other than the one the context targets: no resolution may be
// recorded for a version never applied. Releases the handled-version latch too, or the deal could never
// be re-detected.
export const clearPendingReview = () => ({
    type: CLEAR_PENDING_REVIEW
});

// Intent only, with no payment meta: the middleware decides what happens, because the sequence differs
// by trigger and a component cannot see which one applies.
export const resolvePendingReviewRequested = () => ({
    type: RESOLVE_PENDING_REVIEW_REQUESTED
});

// `meta.middleware.payment` is what makes this a real payment trigger. Exempt from the payment sequence
// guard — see PAYMENT_SEQUENCE_EXEMPT_TRIGGERS.
export const resolvePendingDealerReviewPayment = () => ({
    type: RESOLVE_PENDING_DEALER_REVIEW_PAYMENT,
    meta: {
        middleware: {
            payment: true
        }
    }
});

export const clearSocketEventNotification = () => ({
    type: CLEAR_SOCKET_EVENT_NOTIFICATION
});

export const updatedOfferType =
    (offerType: OfferType): any =>
    (dispatch: any, getState: any) => {
        const state = getState();

        if (getIsUserProgramQuotes(state)) {
            dispatch({
                type: 'UPDATE_OVERRIDES',
                payload: offerType
            });
        }

        dispatch(offerRedux.updatedOfferType(offerType, { suppressAnalytics: true }));
    };

export const updatedTotalTaxOverride = (overrideValue?: number, manualTotalTaxEdited: boolean = false) => {
    return {
        meta: {
            middleware: {
                payment: true
            }
        },
        type: UPDATED_TOTAL_TAX_OVERRIDE,
        payload: {
            manualTotalTaxEdited,
            manualTotalTax: overrideValue
        }
    };
};

export const updatedTotalTaxUpfrontOverride = (overrideValue: boolean) => {
    return {
        meta: {
            middleware: {
                payment: true
            }
        },
        type: UPDATED_TOTAL_TAX_UPFRONT_OVERRIDE,
        payload: overrideValue
    };
};

export const updatedFeesOverride = (fees: FeeOverride[], payment = true, abortSignal?: AbortSignal) => {
    return {
        type: UPDATE_PAYMENT_FEES_OVERRIDE,
        payload: fees,
        meta: {
            // When provided, this signal lets a newer fee recalculation abort the previous in-flight
            // one (see FeesCard). calculatePayments forwards it to fetchPayments; an aborted request
            // resolves to PAYMENT_ABORTED instead of clobbering the latest result.
            ...(abortSignal ? { abortSignal } : {}),
            middleware: {
                payment
            }
        }
    };
};

export const updateDealRefId = ({ dealRefId }: { dealRefId: string }) => {
    return {
        type: UPDATE_DEAL_REF_ID,
        payload: {
            dealRefId
        }
    };
};

export const updatePaymentCallPendingState = (paymentCallPending: boolean) => {
    return {
        type: SET_IS_PAYMENT_CALL_PENDING,
        payload: {
            paymentCallPending
        }
    };
};

export const updatePaymentInFlightCount = (count: number) => ({
    type: SET_PAYMENT_IN_FLIGHT_COUNT,
    payload: { count }
});

export const updateDeferredDownPayment = (deferredDownPaymentAmount: number, deferredDownPaymentDate: string) => {
    return {
        type: UPDATE_DEFERRED_DOWN_PAYMENT,
        payload: {
            amount: deferredDownPaymentAmount,
            paymentDate: deferredDownPaymentDate
        }
    };
};

export const setSoftPullConsentProvided = (value: boolean) => {
    return {
        type: SET_SOFT_PULL_CONSENT_PROVIDED,
        payload: value
    };
};

export const setCreditRatingDisplayType = (value: CreditRatingDisplayType) => ({
    type: SET_CREDIT_RATING_DISPLAY_TYPE,
    payload: value
});

export const setShowSoftPullConsentModal = (show: boolean) => {
    return {
        type: SET_SHOW_SOFT_PULL_CONSENT_MODAL,
        payload: show
    };
};
